import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

// Data
import { IColumnMap, IImportMatcherContainer, ISampleRow } from './import-matcher.interface';

// Components
import { ImportMatcher } from './import-matcher.component';
import {
  confirmAllColumnMappings,
  createColumnFuse,
  discardUnconfirmedColumnMappings,
  findEmptyColumnIndices,
  findUnmatchedColumns,
  matchColumnByHeader,
  orderColumnIndicesByRequiredFirst
} from '../importer.helpers';
import { ImportCell } from '../importer.interface';

const SAMPLE_SIZE = 3;

export const ImportMatcherContainer: React.FC<IImportMatcherContainer> = ({
  requiredColumns,
  expectedColumns,
  rows,
  headerRowIndex,
  columnMap,
  setColumnMap,
  completeMatching,
  columnSelectionOptions
}) => {
  const [columnOrder, setColumnOrder] = useState<number[]>([]);
  const [overflowColumns, setOverflowColumns] = useState<number[]>([]);
  const [overflowRows, setOverflowRows] = useState<number[]>([]);
  const [sample, setSample] = useState<ISampleRow[]>([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Pre-map columns by taking a best guess
    const fuse = createColumnFuse(expectedColumns);
    const initialColumnMap: IColumnMap[] = [];

    if (rows.length && headerRowIndex !== null) {
      const headerRow = rows[headerRowIndex];

      headerRow.forEach((cell: ImportCell, index: number) => {
        initialColumnMap[index] = { column: matchColumnByHeader(cell || '', expectedColumns, fuse), isLocked: false, isIgnored: false };
      });
    }

    // Pull a sample to show when selecting columns
    const currSample: ISampleRow[] = [];
    const currOverflowRows: number[] = [];
    // Zero when the file has no header row, in which case no row can overflow one.
    const headerColumnCount = initialColumnMap.length;
    let currMaxColumns = 0;

    for (let i = 0; i < rows.length; i += 1) {
      if (i !== headerRowIndex) {
        const row = rows[i];

        // A ragged row whose extra cells are all blank — a trailing comma — carries nothing past
        // the header, so it is not reported as overflowing.
        if (headerColumnCount > 0 && row.slice(headerColumnCount).some(cell => !!cell?.trim())) {
          currOverflowRows.push(i + 1);
        }

        if (currMaxColumns <= row.length) {
          currSample.push({rowNum: i + 1, rowData: row});
          currMaxColumns = row.length;
        }

        if (currSample.length > SAMPLE_SIZE) {
          currSample.pop();
        }
      }
    }

    if (initialColumnMap.length === 0) {
      for (let i = 0; i < currMaxColumns; i += 1) {
        initialColumnMap[i] = { isLocked: false, isIgnored: false };
      }
    }

    // Rows are padded to the header width upstream, but a ragged row can still run wider; the max
    // keeps the overflow range ascending and gives every columnMap entry a card.
    const mappableColumnCount = Math.max(currMaxColumns, initialColumnMap.length);
    // A column with no values imports nothing, so it is dropped everywhere rather than offered as a
    // choice: locked as ignored here, and left out of the display order and overflow report below.
    const emptyColumns = new Set(findEmptyColumnIndices(rows, headerRowIndex, mappableColumnCount));

    emptyColumns.forEach(index => {
      if (initialColumnMap[index]) initialColumnMap[index] = { isLocked: true, isIgnored: true };
    });

    setColumnMap(initialColumnMap);
    // Frozen for the step: re-sorting on every edit would slide the card the user is working on
    // out from under the cursor the moment they pick a required field.
    setColumnOrder(orderColumnIndicesByRequiredFirst(initialColumnMap, requiredColumns, mappableColumnCount)
      .filter(index => !emptyColumns.has(index)));
    // A ragged CSV can carry more fields in a data row than the header row has columns; those
    // trailing values have no header to match against, so they are reported rather than mapped.
    setOverflowColumns(_.range(initialColumnMap.length, mappableColumnCount).filter(index => !emptyColumns.has(index)));
    setOverflowRows(currOverflowRows);
    setSample(currSample);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.length, headerRowIndex]);

  // Derived, not state: an effect would settle these a render after the confirmed card relocates,
  // shifting the notices above the list mid-scroll.
  const unmetRequirements = useMemo(
    () => findUnmatchedColumns(requiredColumns, columnMap),
    [requiredColumns, columnMap]
  );

  const unmetFields = useMemo(
    () => findUnmatchedColumns(expectedColumns, columnMap),
    [expectedColumns, columnMap]
  );

  const setColumnMapping = useCallback((columnIndex: number, mapping: IColumnMap) => {
    const nextMap = [...columnMap];
    nextMap[columnIndex] = mapping;

    setColumnMap(nextMap);
  }, [columnMap, setColumnMap]);

  const confirmAllMappings = useCallback(
    () => setColumnMap(confirmAllColumnMappings(columnMap)),
    [columnMap, setColumnMap]
  );

  const discardUnconfirmedMappings = useCallback(
    () => setColumnMap(discardUnconfirmedColumnMappings(columnMap)),
    [columnMap, setColumnMap]
  );

  return <ImportMatcher
    headerRowIndex={headerRowIndex}
    headerRow={headerRowIndex !== null && rows[headerRowIndex]}
    sample={sample}
    unmetRequirements={unmetRequirements}
    unmetFields={unmetFields}
    expectedColumns={expectedColumns}
    columnMap={columnMap}
    columnOrder={columnOrder}
    overflowColumns={overflowColumns}
    overflowRows={overflowRows}
    setColumnMapping={setColumnMapping}
    confirmAllMappings={confirmAllMappings}
    discardUnconfirmedMappings={discardUnconfirmedMappings}
    rows={rows}
    completeMatching={completeMatching}
    columnSelectionOptions={columnSelectionOptions}
  />;
};
